// frontend/src/lib/universalRiskEngine_v3.js
// V3 deterministic engine (MVP): produces scenario outputs + summary.
// Focus: US_STATE aggregation modes (MAX default; SUM/WEIGHTED supported).
// NOTE: No FX normalization in MVP; scenarios remain in native currency.

function nowIso() {
  try {
    return new Date().toISOString();
  } catch {
    return String(Date.now());
  }
}

function toNum(x, fb = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : fb;
}

function clamp(n, lo, hi) {
  const x = toNum(n, lo);
  return Math.max(lo, Math.min(hi, x));
}

function setToArray(x) {
  if (x instanceof Set) return Array.from(x);
  if (Array.isArray(x)) return x;
  return [];
}

function money(n) {
  const x = Number(n);
  return Number.isFinite(x) ? Math.round(x) : 0;
}

function currencyForScenarioKey(key) {
  if (key === "EU") return { currency_code: "EUR", currency_symbol: "€" };
  if (key === "UK") return { currency_code: "GBP", currency_symbol: "£" };
  return { currency_code: "USD", currency_symbol: "$" };
}

function severityMultiplier(tier) {
  // 1..5 => 0.70..1.30 deterministic band
  const t = clamp(tier, 1, 5);
  const map = { 1: 0.7, 2: 0.85, 3: 1.0, 4: 1.15, 5: 1.3 };
  return map[Math.round(t)] ?? 1.0;
}

function calcImpactMultiplier(peoplePerMonth) {
  // Deterministic simple curve for MVP: 1.0 .. 2.0
  const p = Math.max(0, toNum(peoplePerMonth, 0));
  // 0 => 1.0, 50k => ~2.0
  return clamp(1 + p / 50000, 1, 2);
}

function calcLiveRiskMultiplier(eventsSnapshot) {
  // Deterministic placeholder; keep 1.00 unless snapshot provides a numeric multiplier.
  const direct = toNum(eventsSnapshot?.live_risk_multiplier, NaN);
  return Number.isFinite(direct) ? clamp(direct, 0.75, 1.5) : 1.0;
}

function baseFineForRegime(regimeKey, enforcement) {
  // Deterministic placeholder statutory bases for MVP
  // (Later: swap to statutory tables + per-record logic per regime.)
  const v = (enforcement?.violation_type || "").toString().toLowerCase();
  const privacyBoost = v.includes("privacy") || v.includes("data") ? 1.15 : 1.0;

  if (regimeKey === "EU") return money(250000 * privacyBoost);
  if (regimeKey === "UK") return money(200000 * privacyBoost);
  if (regimeKey === "US_FED") return money(150000 * privacyBoost);
  if (regimeKey === "US_STATE") return money(75000 * privacyBoost);

  return money(100000 * privacyBoost);
}

function computeScenario({ regimeKey, enabled, peoplePerMonth, severityTier, enforcement, eventsSnapshot }) {
  const cur = currencyForScenarioKey(regimeKey);
  if (!enabled) {
    return {
      enabled: false,
      regime_key: regimeKey,
      display_name:
        regimeKey === "EU"
          ? "European Union"
          : regimeKey === "UK"
          ? "United Kingdom"
          : regimeKey === "US_STATE"
          ? "US State-by-State"
          : "US Federal",
      ...cur,
      statutory_ceiling_visible_native: 0,
      floor_native: 0,
      worst_case_native: 0,
      net_native: 0,
    };
  }

  const baseFine = baseFineForRegime(regimeKey, enforcement);
  const impact = calcImpactMultiplier(peoplePerMonth);
  const sev = severityMultiplier(severityTier);
  const live = calcLiveRiskMultiplier(eventsSnapshot);

  // MVP structure:
  // worst = base * impact * severity * live
  const worst = money(baseFine * impact * sev * live);

  // Visible statutory ceiling (cap) — keep deterministic and >= worst
  const ceiling = money(Math.max(worst, baseFine * 10));

  // Floor is guardrailed 65% of worst (your MVP spec)
  const floor = money(worst * 0.65);

  // Net: deterministic remediation credit placeholder (12% reduction), guardrailed not below floor
  const netRaw = money(worst * 0.88);
  const net = money(Math.max(floor, Math.min(worst, netRaw)));

  return {
    enabled: true,
    regime_key: regimeKey,
    display_name:
      regimeKey === "EU"
        ? "European Union"
        : regimeKey === "UK"
        ? "United Kingdom"
        : regimeKey === "US_STATE"
        ? "US State-by-State"
        : "US Federal",
    ...cur,

    statutory_ceiling_visible_native: ceiling,
    floor_native: floor,
    worst_case_native: worst,
    net_native: net,

    // (optional, useful for Legal Mode later)
    headline_base_fine_native: baseFine,
    impact_multiplier: impact,
    live_risk_multiplier: live,
    severity_multiplier: sev,
  };
}

function computeUsStateScenario({
  enabled,
  selectedStates,
  aggregationMode,
  peoplePerMonth,
  severityTier,
  enforcement,
  eventsSnapshot,
}) {
  const cur = currencyForScenarioKey("US_STATE");
  const states = setToArray(selectedStates).filter(Boolean);

  if (!enabled) {
    return {
      enabled: false,
      regime_key: "US_STATE",
      display_name: "US State-by-State",
      ...cur,
      statutory_ceiling_visible_native: 0,
      floor_native: 0,
      worst_case_native: 0,
      net_native: 0,
      aggregation_mode: (aggregationMode || "MAX").toUpperCase(),
      states_selected: states,
      state_breakdown: [],
    };
  }

  // If enabled but no states selected, keep scenario enabled but mark as incomplete with zeros.
  if (states.length === 0) {
    return {
      enabled: true,
      regime_key: "US_STATE",
      display_name: "US State-by-State",
      ...cur,
      statutory_ceiling_visible_native: 0,
      floor_native: 0,
      worst_case_native: 0,
      net_native: 0,
      aggregation_mode: (aggregationMode || "MAX").toUpperCase(),
      states_selected: [],
      state_breakdown: [],
      note: "Select at least one state to compute US State-by-State exposure.",
    };
  }

  const mode = (aggregationMode || "MAX").toString().toUpperCase();

  // Deterministic per-state base: derived from a regime base + small stable state variation
  // (Later: replace with true state statutes.)
  const regimeBase = baseFineForRegime("US_STATE", enforcement);

  // Shared multipliers for all states (MVP)
  const impact = calcImpactMultiplier(peoplePerMonth);
  const sev = severityMultiplier(severityTier);
  const live = calcLiveRiskMultiplier(eventsSnapshot);

  const rows = states.map((st) => {
    const s = st.toString().toUpperCase();

    // Stable tiny variation by state code hash (no randomness)
    let hash = 0;
    for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) % 997;
    const bump = 0.90 + (hash / 997) * 0.20; // 0.90..1.10

    const base = money(regimeBase * bump);
    const exposure = money(base * impact * sev * live);

    return {
      state: s,
      base_fine_usd: base,
      exposure_ceiling_usd: exposure,
      weight: null, // filled for WEIGHTED
    };
  });

  // Default equal weights if WEIGHTED and no weights provided.
  if (mode === "WEIGHTED") {
    const w = 1 / rows.length;
    rows.forEach((r) => (r.weight = w));
  }

  // Aggregate
  let worst = 0;
  if (mode === "SUM") {
    worst = money(rows.reduce((acc, r) => acc + toNum(r.exposure_ceiling_usd, 0), 0));
  } else if (mode === "WEIGHTED") {
    // Weighted sum (weights sum to 1). Deterministic equal-weight MVP.
    worst = money(
      rows.reduce((acc, r) => acc + toNum(r.exposure_ceiling_usd, 0) * toNum(r.weight, 0), 0)
    );
  } else {
    // MAX (conservative default)
    worst = money(Math.max(...rows.map((r) => toNum(r.exposure_ceiling_usd, 0))));
  }

  const ceiling = money(Math.max(worst, regimeBase * 10));
  const floor = money(worst * 0.65);
  const netRaw = money(worst * 0.88);
  const net = money(Math.max(floor, Math.min(worst, netRaw)));

  return {
    enabled: true,
    regime_key: "US_STATE",
    display_name: "US State-by-State",
    ...cur,

    statutory_ceiling_visible_native: ceiling,
    floor_native: floor,
    worst_case_native: worst,
    net_native: net,

    aggregation_mode: mode,
    states_selected: states,
    state_breakdown: rows,
    headline_base_fine_native: regimeBase,
    impact_multiplier: impact,
    live_risk_multiplier: live,
    severity_multiplier: sev,
  };
}

/**
 * Public API (UniversalAuditTool expects this name)
 */
export function runUniversalRiskEngineV3(input = {}) {
  const {
    selectedJurKeys,
    selectedStates,
    usStateAggregationMode,

    peoplePerMonth,
    annualRevenue, // reserved for later
    severityTier,
    enforcementInputs,
    eventsSnapshot,
  } = input;

  const jurKeys = setToArray(selectedJurKeys).map((x) => x?.toString()).filter(Boolean);
  const enabled = {
    US_FED: jurKeys.includes("US_FED") || jurKeys.includes("US") || jurKeys.includes("us") || jurKeys.length === 0,
    US_STATE: jurKeys.includes("US_STATE") || jurKeys.includes("us_state") || jurKeys.includes("US_STATE_BY_STATE"),
    EU: jurKeys.includes("EU") || jurKeys.includes("eu"),
    UK: jurKeys.includes("UK") || jurKeys.includes("uk"),
  };

  // Build scenarios
  const scenarios = {
    US_FED: computeScenario({
      regimeKey: "US_FED",
      enabled: !!enabled.US_FED,
      peoplePerMonth,
      severityTier,
      enforcement: enforcementInputs,
      eventsSnapshot,
    }),
    US_STATE: computeUsStateScenario({
      enabled: !!enabled.US_STATE,
      selectedStates,
      aggregationMode: usStateAggregationMode || "MAX",
      peoplePerMonth,
      severityTier,
      enforcement: enforcementInputs,
      eventsSnapshot,
    }),
    EU: computeScenario({
      regimeKey: "EU",
      enabled: !!enabled.EU,
      peoplePerMonth,
      severityTier,
      enforcement: enforcementInputs,
      eventsSnapshot,
    }),
    UK: computeScenario({
      regimeKey: "UK",
      enabled: !!enabled.UK,
      peoplePerMonth,
      severityTier,
      enforcement: enforcementInputs,
      eventsSnapshot,
    }),
  };

  // Headline basis:
  // Prefer US_STATE if enabled and has states; else pick first enabled in priority order.
  let headline_basis = "US_FED";
  if (scenarios.US_STATE.enabled && (scenarios.US_STATE.states_selected?.length || 0) > 0) headline_basis = "US_STATE";
  else if (scenarios.UK.enabled) headline_basis = "UK";
  else if (scenarios.EU.enabled) headline_basis = "EU";
  else if (scenarios.US_FED.enabled) headline_basis = "US_FED";

  const headlineScenario = scenarios[headline_basis] || scenarios.US_FED;
  const cur = currencyForScenarioKey(headline_basis);

  const headline_exposure_native = money(headlineScenario?.worst_case_native ?? 0);
  const floor_native = money(headlineScenario?.floor_native ?? 0);
  const statutory_ceiling_visible_native = money(headlineScenario?.statutory_ceiling_visible_native ?? 0);
  const net_native = money(headlineScenario?.net_native ?? 0);

  const summary = {
    engine_version: "v3",
    generated_at: nowIso(),

    headline_basis,
    headline_label: "Realistic Worst Case (no FX)",

    ...cur,

    headline_exposure_native,
    floor_native,
    statutory_ceiling_visible_native,
    net_native,

    live_risk_multiplier: calcLiveRiskMultiplier(eventsSnapshot),

    // FYI only: cross-currency stacked totals not FX summed.
    combined_stacked_by_currency: {
      USD: money((scenarios.US_FED?.worst_case_native || 0) + (scenarios.US_STATE?.worst_case_native || 0)),
      EUR: money(scenarios.EU?.worst_case_native || 0),
      GBP: money(scenarios.UK?.worst_case_native || 0),
    },

    notes: [],
  };

  return {
    generated_at: summary.generated_at,
    summary,
    scenarios,
    inputs_echo: {
      peoplePerMonth: toNum(peoplePerMonth, 0),
      annualRevenue: toNum(annualRevenue, 0),
      severityTier: toNum(severityTier, 3),
      usStateAggregationMode: (usStateAggregationMode || "MAX").toString().toUpperCase(),
      selectedStates: setToArray(selectedStates),
      selectedJurKeys: jurKeys,
    },
  };
}

export default runUniversalRiskEngineV3;
